import type { WorkspaceModel } from "./useWorkspace";
import type { TLImageShape } from "@tldraw/tlschema";
import { variantReview, voteFor } from "../shared/variant-review";
export function VariantReviewControls({ model }: { model: WorkspaceModel }) {
  const { editor, selected, user } = model;
  const images = selected.flatMap((shape) =>
    shape.type === "image"
      ? [shape]
      : shape.type === "group"
        ? editor
            .getSortedChildIdsForParent(shape.id)
            .map((id) => editor.getShape(id))
            .filter((s): s is TLImageShape => s?.type === "image")
        : [],
  );
  if (
    !model.canEdit ||
    !images.length ||
    images.some((s) => editor.isShapeOrAncestorLocked(s))
  )
    return null;
  const enable = () => {
    editor.markHistoryStoppingPoint("Enable variant picks");
    editor.updateShapes(
      images.map((s) => ({
        id: s.id,
        type: "image" as const,
        meta: { ...s.meta, variantReviewEnabled: true },
      })),
    );
  };
  if (images.length > 1)
    return <button onClick={enable}>Enable picks on selected images</button>;
  const image = images[0],
    review = variantReview(image);
  const change = (meta: typeof image.meta, label: string) => {
    editor.markHistoryStoppingPoint(label);
    editor.updateShape({
      id: image.id,
      type: "image",
      meta: { ...image.meta, ...meta },
    });
  };
  return (
    <fieldset>
      <legend>Variant review</legend>
      <label>
        <input
          type="checkbox"
          checked={review.enabled}
          onChange={(event) =>
            change(
              { variantReviewEnabled: event.target.checked },
              "Show variant pick",
            )
          }
        />
        Show pick on image
      </label>
      <label>
        <input
          type="checkbox"
          checked={review.selected}
          onChange={(event) =>
            change(
              {
                variantSelected: event.target.checked,
                variantStatus: event.target.checked ? "selected" : "undecided",
              },
              "Pick variant",
            )
          }
        />
        Shared pick
      </label>
      <label>
        Status{" "}
        <select
          value={review.status}
          onChange={(event) =>
            change(
              {
                variantStatus: event.target.value,
                variantSelected: event.target.value === "selected",
              },
              "Change variant status",
            )
          }
        >
          <option value="undecided">Undecided</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="selected">Selected</option>
          <option value="rejected">Rejected</option>
        </select>
      </label>
      <button
        aria-pressed={review.votes[user.id]?.selected === true}
        onClick={() =>
          change(
            {
              variantVotes: voteFor(
                image.meta,
                user,
                review.votes[user.id]?.selected ? null : true,
              ),
            },
            "Vote for variant",
          )
        }
      >
        {review.votes[user.id]?.selected
          ? "Remove my vote"
          : "Vote for this option"}
      </button>
      <p>
        {Object.values(review.votes)
          .filter((v) => v.selected)
          .map((v) => v.name)
          .join(", ") || "No personal votes"}
      </p>
    </fieldset>
  );
}
